import {Component, Input, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {UserService} from '../../services/user/user.service';
import {SettingsService} from '../../services/settings/settings.service';
import {InternationalizationService} from '../../services/internationalization/internationalization.service';
import {UserProfileService} from '../../services/user/user-profile.service';
import {FieldService} from '../../services/field/field.service';
import {SelectEventArgs} from '@syncfusion/ej2-angular-dropdowns';
import {$translate} from '../../services/internationalization/localize.service';
import {KnockoutFieldModel} from '../knockout-field/knockout-field.model';
import {AlertService} from "../../services/alert/alert.service";

interface ImpersonateUserOption {
    id: string;
    userName: string;
    fullName: string;
    profilePhoto: string;
    initials: string;
}

@Component({
    selector: 'sb-impersonate-dialog',
    templateUrl: './impersonate-dialog.component.html',
    styleUrls: ['./impersonate-dialog.component.scss']
})
export class ImpersonateDialogComponent implements OnInit {

    constructor(private settingsService: SettingsService,
                private i18nService: InternationalizationService,
                private userService: UserService,
                private userProfileService: UserProfileService,
                private fieldService: FieldService,
                private alertService: AlertService) {
    }

    public recentSelectedOption: ImpersonateUserOption;
    public recentFilteredOptions: ImpersonateUserOption[] = [];
    private selectedUser: string = null;

    // I18n Labels
    recentImpersonationsLabel: string;
    headerLabel: string;
    impersonateButtonLabel: string;
    selectUserLabel: string;
    cancelButtonLabel: string;
    noMatchingDataLabel: string;
    errorLabel: string;
    reloadLabel: string;

    private isImpersonated = false;
    private showModal = false;

    disableImpersonateButton = true;

    public userReferenceField: KnockoutFieldModel = null;

    async ngOnInit(): Promise<void> {
        this.getRecentImpersonations()
            .then(data => {
                this.recentFilteredOptions = data;
            });

        this.userProfileService.getUserProfile(false)
            .then(profile => {
                this.isImpersonated = profile['isImpersonated'] === true;
            });

        this.updateMessages();
    }

    async showDialog(): Promise<void> {
        if (this.userReferenceField == null) {
            this.userReferenceField = this.fieldService.createReferenceField(
                'User',
                this.selectUserLabel,
                'User',
                {
                    valueChangeCallback: (value) => {
                        this.selectedUser = value;
                        this.checkImpersonateButtonState();
                    },
                    rendererProperties: {
                        additionalFilterQuery: {"nor":[
                            {"fieldName": "MergedRoles.RoleName","operator": "=","value": "Administrator"},
                            {"fieldName": "id","operator": "IN","value": ["0a01010f43091ca081430985781f0000","4fa0a53d386e4236982412cb3f984d23"]}
                        ]}
                    }
                }
            );
        }
        this.showModal = true;
    }

    onClose(): void {
        this.showModal = false;
    }

    impersonate(): void {
        if (this.selectedUser != null) {
            this.userService
                .impersonate(this.selectedUser)
                .then(() => this.impersonateSuccess())
                .catch(() => this.impersonateError());
        } else if (this.recentSelectedOption != null) {
            this.userService
                .impersonate(this.recentSelectedOption.id)
                .then(() => this.impersonateSuccess())
                .catch(() => this.impersonateError());
        }
    }

    checkImpersonateButtonState(){
        this.disableImpersonateButton = (this.selectedUser == null && this.recentSelectedOption == null);
    }

    impersonateSuccess(): void {
        this.alertService.success(this.reloadLabel);
    }

    impersonateError() {
        this.alertService.alert(this.errorLabel);
    }

    private async getRecentImpersonations() {
        return await this.userProfileService.getRecentImpersonations();
    }

    recentSelectedOptionSelected($event: any) {
        this.recentSelectedOption = $event.data as ImpersonateUserOption;
        this.checkImpersonateButtonState();
    }

    private updateMessages() {
        this.recentImpersonationsLabel = $translate('system.impersonation.recent')`Recent impersonations`;
        this.headerLabel = $translate('system.impersonation.title')`Impersonation`;
        this.impersonateButtonLabel = $translate("system.impersonation.impersonate_button")`Impersonate`;
        this.selectUserLabel = $translate("system.impersonation.select_user")`Select user`;
        this.noMatchingDataLabel = $translate("system.impersonation.recent_no_match")`No recent impersonations`;
        this.errorLabel = $translate('system.impersonation.error')`Could not impersonate the requested user.`;
        this.reloadLabel = $translate('system.impersonation.success_message')`Success: Reloading UI...`;
        this.cancelButtonLabel = $translate("common.button.cancel")`Cancel`;
    }
}
